import { useId, useState } from "react";
import { useParams } from "react-router-dom";
import { PiStar, PiStarFill } from "react-icons/pi";
import { lstr } from "./localization";
import {
  MAX_STORY_RATING,
  useRateStoryMutation,
  useStoryRatingQuery,
} from "./queries";

const STORY_RATINGS = Array.from(
  { length: MAX_STORY_RATING },
  (_, idx) => idx + 1,
);

// StoryRating lets a signed-in reader rate the story being read with stars.
// The stars are a native radio group, so arrow keys move between ratings;
// every choice is saved right away.
export function StoryRating({ l }: { l: string }) {
  const strings = lstr(l);
  // Only rendered on the story routes, see StoryComponent.
  const storyId = useParams().storyId!;
  const ratingQuery = useStoryRatingQuery(storyId);
  const rateMutation = useRateStoryMutation(storyId);
  // A mouse pointing at a star previews that rating.
  const [previewedRating, setPreviewedRating] = useState<number | null>(null);
  const questionId = useId();

  const rating = ratingQuery.data ?? null;
  const shownRating = previewedRating ?? rating ?? 0;

  let statusMessage = "";
  if (rateMutation.isError) {
    statusMessage = strings.story_rating_error;
  } else if (!rateMutation.isIdle) {
    statusMessage = strings.story_rating_thanks;
  }

  return (
    <div className="card mt-8 flex w-full flex-col items-center px-6 py-5">
      <p id={questionId} className="font-semibold text-main-text">
        {strings.story_rating_question}
      </p>
      <div
        role="radiogroup"
        aria-labelledby={questionId}
        className="mt-2 flex"
        onPointerLeave={() => setPreviewedRating(null)}
      >
        {STORY_RATINGS.map((stars) => (
          <label
            key={stars}
            className="relative cursor-pointer"
            onPointerEnter={(event) => {
              // Touch has no hover; a preview would stick after the tap.
              if (event.pointerType === "mouse") setPreviewedRating(stars);
            }}
          >
            <input
              type="radio"
              name={`story-rating-${questionId}`}
              value={stars}
              checked={rating === stars}
              onChange={() => rateMutation.mutate(stars)}
              aria-label={strings.story_rating_star_label
                .replace("{rating}", String(stars))
                .replace("{max}", String(MAX_STORY_RATING))}
              className="peer sr-only"
            />
            <span className="block rounded-lg p-1.5 transition-transform active:scale-90 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-primary">
              {stars <= shownRating ? (
                // At least 3:1 against the card in both themes, unlike the
                // lighter highlight gold.
                <PiStarFill
                  size={32}
                  className="text-amber-600 dark:text-amber-400"
                />
              ) : (
                <PiStar size={32} className="text-secondary-text" />
              )}
            </span>
          </label>
        ))}
      </div>
      <p
        aria-live="polite"
        className={`mt-1 min-h-5 text-sm ${
          rateMutation.isError ? "text-error" : "text-secondary-text"
        }`}
      >
        {statusMessage}
      </p>
    </div>
  );
}
